import type { DrawContext } from './draw';
import { strand } from './draw';
import type { Box } from './labels';
import type { View } from './project';
import { toScreen } from './project';

/*
 * THE OPEN NAME'S FAN — every tie out of the figure the reader has opened, at full strength.
 *
 * Ported from `reference/universe-v8/engine.js` — `drawFrame` lines 1828–1880.
 *
 * THE ONE LAYER THAT ANSWERS A QUESTION THE READER ASKED. Everything else on the map is offered:
 * the wash, the web, the names, the dust. This is drawn because somebody opened a figure and wants
 * to know what it is connected to — which is why it obeys none of the ambient web's economies.
 *
 * No fan cap, so a hub shows all of its ties rather than four. No importance gate, so a tie to a
 * minor name survives. No dimming, so these are the brightest strands on the screen; the ambient
 * web dims ITSELF by 0.5/0.42/0.26 whenever anything is open, precisely so this one stands out
 * without being drawn any harder. And `web.ts` skips the open name's ties entirely, so a strand is
 * never drawn twice.
 *
 * FOUR THINGS ARE DRAWN, and they say four different things:
 *
 *   the strands   what this figure is tied to, coloured by how much of it the reader has walked
 *   the bead      one strand at a time, travelling — an invitation to follow, not a decoration
 *   the passages  where the tie is WRITTEN: chapter and verse, on the six longest ties
 *   the rings     one steady, one breathing, so the open name is found again after a pan
 *
 * THE CLASSES ARE NOT THE AMBIENT WEB'S. There, a tie is walked when BOTH ends have been opened.
 * Here the near end is the open figure itself, so only the FAR end is asked about, and there are
 * three answers rather than two: walked it, merely seen it, or neither. A soft tie is a rumour
 * whatever the reader has walked — an inferred connection does not become firm by being visited.
 */

/** Their turn, in radians, exactly as written in the reference. */
const TAU = 6.2832;

/** A name, as the fan needs it. */
export interface FanNode {
  x: number;
  y: number;
  /** Its radius in WORLD units — what sets the gap the strands leave around it. */
  r: number;
}

/**
 * WHAT THE READER HAS WALKED, AND WHAT THE TIES SAY — all data, none of it theirs.
 *
 * `refs` is keyed by the unordered pair `min:max`, exactly as their `edgeRef` is, and holds the
 * passage a tie is written in. Those strings are the most obviously ours of anything in the port:
 * theirs are Genesis and Qur'an references invented for the demo.
 */
export interface FanFog {
  /** Whether the far end has been opened. */
  walked: (id: number) => boolean;
  /** Whether the far end has been passed near enough to be recognised. */
  seen: (id: number) => boolean;
  /** The ties inferred rather than stated, keyed `min:max`. */
  weak?: ReadonlySet<string>;
  /** Where a tie is written, keyed `min:max`. */
  refs?: ReadonlyMap<string, string>;
}

/**
 * THE ZOOMS THIS LAYER CHANGES AT — arguments, because a zoom is pixels per world unit.
 *
 * Their 0.9 is the twenty-eighth absolute zoom lifted out of this engine. A world ten times the
 * size is read at a tenth the zoom, and a passage reference drawn at the wrong range is either
 * unreadable or the only thing on the screen.
 */
/**
 * THE THRESHOLDS THIS LAYER MEASURES AGAINST — zooms AND one world length, which is why it is no
 * longer called `FanZooms`.
 *
 * `refsFrom` is a zoom and `localTie` is a distance in world units. Naming the pair after one of
 * them is how this repository has repeatedly ended up comparing a quantity against a threshold that
 * was never about it — the floor, `revealAll`, `sharedAtLeast`, and `refsFrom` itself, which is
 * rescaled by the rim and documented below as being the wrong quantity for the job.
 */
export interface FanLimits {
  /**
   * Below this the passage references are not drawn: the fan is legible, the writing is not.
   *
   * RESCALED BY THE RIM, AND THE RIM IS THE WRONG QUANTITY — measured 2026-08-21, and left as it is
   * because the fix is a decision rather than a correction.
   *
   * A passage is written at the MIDPOINT of its strand (engine.js:1844, ported exactly below). So
   * whether it can be written at all is a question about TIE LENGTH against the frame, and this
   * threshold is rescaled by the rim. On their map the two agree; on ours they do not:
   *
   *     the frame at the zoom opening a name lands on   4.9% of the rim across
   *     a tie                                           median 27% of the rim, Abraham's 32%
   *
   * A typical strand is six frame-widths long, so its midpoint is three frames off-screen and the
   * margin test throws it away. Swept over every zoom with Abraham open, the fan wrote ONE citation
   * of a possible six, in a slit around z 3.7 that a reader crosses in a fraction of a second on the
   * way in. It is the shape `CLAUDE.md` records: ported, covered, and unreachable — every unit test
   * here passes because each is handed a fixture with the far end in frame.
   *
   * AND THEIRS ARE SHORT BY CONSTRUCTION, which is the half that settles it. Their ties are built
   * `byRegion` (engine.js:405–412): each name is tied to a weight-neighbour inside its own region,
   * and a region is a contiguous patch of their map — so a tie is local, and its midpoint is near
   * the name it belongs to. They are invented for the demo besides.
   *
   * Ours are real shared passages, and the wheel puts each text on its own side, so a figure named
   * by five texts reaches across the whole map. The layout is doing its job and the mock's ties
   * were never doing this one. It is a data difference, not a defect in the port.
   *
   * TWO WAYS OUT, neither taken yet. Write the passage a fixed reach along the strand instead of at
   * its midpoint, or rescale this threshold by the median tie rather than by the rim, which is the
   * quantity it is really about.
   *
   * THE FIRST WAS TRIED AND IS NOT A ONE-LINER. Moving every passage to one reach from the open name
   * puts them all on a ring around it, and six tests in this file went red — including the one
   * pinning that SIX are written, longest-first. The count is not incidental: where a label sits
   * decides whether it clears the margin and the chrome, so moving them changes how many survive.
   * Whatever replaces the midpoint has to answer that, not just the geometry.
   *
   * WHAT ACTUALLY WORKS, built and measured and then reverted, so the next attempt starts here:
   *
   *   1. Keep theirs — the midpoint — whenever it is on screen. That is most of their fixture.
   *   2. Otherwise bisect along the SAME curve for the furthest point that is. Eight steps land
   *      within a fifth of a percent, finer than the text being placed, and the curve leaves the
   *      viewport exactly once so a bisection is sound. Each passage stays on its own strand
   *      rather than joining a ring, which is what the ring attempt got wrong.
   *   3. Do it ONLY where their design never reaches, or every differential in this file breaks.
   *
   * Step 3 is the whole difficulty. Gated on the strand's SCREEN length against the viewport
   * diagonal, ten failures became one — and the one left is `draws exactly what theirs draws, at
   * every zoom and every moment`, because at a close enough zoom their short ties exceed the
   * diagonal too and ours then writes two citations theirs drops. 231 draw calls against 229.
   *
   * The gate wants to be the strand's WORLD length, which is where the difference actually lives:
   * theirs are local by construction and ours are not, at any zoom. That means threading the tie
   * length in as a world dimension — an argument with no default, per rule 2 — rather than reading
   * it off the screen. It is a change to this layer's interface and it is the right one.
   */
  refsFrom: number;

  /**
   * HOW LONG A TIE HAS TO BE BEFORE IT IS NOT THE KIND OF TIE THEIR DESIGN WAS BUILT FOR, in WORLD
   * units. No default, because it is a fact about a map's own shape and there is no honest one.
   *
   * This is the argument the note above asks for. Their ties are local by construction — `byRegion`,
   * a name to a weight-neighbour inside its own patch — so a strand's midpoint sits near the name it
   * belongs to and their placement always works. Ours are real shared passages and the wheel puts
   * each text on its own side, so Abraham's run to 32% of the rim and their midpoints are three
   * frames off screen.
   *
   * The gate has to be the WORLD length and not the screen length, which was tried: gated on screen
   * length against the viewport diagonal, ten differential failures became one, and the one left was
   * `draws exactly what theirs draws` — because at a close enough zoom THEIR short ties exceed the
   * diagonal too, and ours then wrote two citations theirs dropped. 231 draw calls against 229. A
   * world length cannot do that: theirs are short at every zoom, so they never take the new path.
   *
   * Pass a length no tie on their map reaches and the layer is theirs exactly.
   */
  localTie: number;
}

/** Where the interface covers the map, so a passage is not written underneath it. */
export interface FanChrome {
  /** How far down the top of the map is clear, and how far up the bottom is. */
  barTop: number;
  barBot: number;
  /** Whether a rectangle is covered by the chrome. `LabelSpace.blocked`, passed as a function. */
  blocked: (box: Box) => boolean;
}

/** How far a figure's disc may be drawn, in screen pixels, whatever the zoom says. */
const DISC_MIN = 4;
const DISC_MAX = 46;

/** The air a strand leaves around the open name, and around the far one. */
const GAP_AIR = 20;
const GAP_FAR = 4;

/** How far the curve bows away from the straight line. The ambient web's own bend. */
const BEND = 0.13;

/** The three classes' head alphas — walked, seen, rumour. The ambient web's, undimmed. */
const HEAD = [0.46, 0.4, 0.26];

/** A rumour is drawn thinner as well as fainter, so the two readings agree. */
const WIDTH_NEAR = 1.3;
const WIDTH_FAR = 0.9;

/** How long the bead spends on each strand before moving to the next. */
const BEAD_CYCLE = 5.5;

/** How far along the bead may start — it never sets out from inside the disc. */
const BEAD_START_CAP = 0.9;

/** The bead itself. Look. */
const BEAD_R = 1.8;
const BEAD_ALPHA = 0.8;
const BEAD_COLOUR = '242,248,255';

/** The passages: how many, in what, and how much air around each. */
const REF_FONT = '400 9.5px "Space Grotesk", sans-serif';
const REF_CAP = 6;
const REF_MARGIN = 8;
const REF_HIT_HALF = 7;
const REF_PAD_X = 4;
const REF_BOX_TOP = 6.5;
const REF_BOX_H = 13;
const REF_BOX = 'rgba(6,8,16,.86)';
const REF_INK = 'rgba(160,178,214,.9)';

/** The two rings around the open name. Look, and an animation rate. */
const RING_COLOUR = '#63e0e8';
const RING_WIDTH = 1.6;
const RING_OUT = 11;
const PULSE_RATE = 2;
const PULSE_BASE = 0.35;
const PULSE_SWING = 0.25;
const PULSE_OUT = 16;
const PULSE_SWELL = 2;
const PULSE_WIDTH = 1;

/**
 * The unordered key a tie is held under, in both `weak` and `refs`.
 *
 * Exported because `hover-fan.ts` reads the same two sets, and a second spelling of this is a
 * second answer to "are these the same tie".
 */
export function pairKey(a: number, b: number): string {
  return `${Math.min(a, b)}:${Math.max(a, b)}`;
}

/**
 * Which of the three classes a tie out of the open name belongs to.
 *
 * Only the FAR end is asked about — the near end is the open figure, which the reader is standing
 * on by definition. A soft tie is a rumour however well the far end is known.
 */
export function fanClass(walked: boolean, seen: boolean, soft: boolean): number {
  if (walked) return 0;
  if (soft) return 2;
  return seen ? 1 : 2;
}

/** One tie, once its ends are on screen. */
interface Strand {
  cls: number;
  /** The far end, in screen pixels. */
  ox: number;
  oy: number;
  /** The control point that bows the curve toward the origin. */
  cx: number;
  cy: number;
}

/** One passage, where it wants to be written and how long its tie is. */
interface Passage {
  ref: string;
  x: number;
  y: number;
  /** The tie's length on screen — what the six are chosen by. */
  d: number;
}

/** A point on a strand's curve at `t`, the quadratic their `drawFan` draws (engine.js:1844). */
function onCurve(
  fs: { x: number; y: number },
  cs: { x: number; y: number },
  os: { x: number; y: number },
  t: number,
): { x: number; y: number } {
  const u = 1 - t;
  return {
    x: u * u * fs.x + 2 * t * u * cs.x + t * t * os.x,
    y: u * u * fs.y + 2 * t * u * cs.y + t * t * os.y,
  };
}

/** Where theirs writes a passage: the middle of the curve. */
const MIDPOINT_T = 0.5;

/**
 * How many times the reach is halved. Eight lands within a fifth of a percent of the curve, finer
 * than the text being placed, so a ninth would move nothing a reader could see.
 */
const REACH_STEPS = 8;

/**
 * THE FURTHEST POINT ALONG THE STRAND THAT IS STILL ON SCREEN, for a tie too long to use its middle.
 *
 * The reader is standing on the open name, so `t = 0` is on screen and the curve leaves the viewport
 * exactly once — which is what makes a bisection sound rather than a guess. Each passage stays on
 * its OWN strand: an earlier attempt put them all at one fixed reach and they formed a ring around
 * the open name, which broke six tests including the one pinning that six are written longest-first.
 *
 * It tests the frame only, not the text width or the chrome. Measuring text is the expensive part
 * and a hub with eighty ties would pay for it eight times over per strand per frame; the exact tests
 * still run afterwards on whatever this returns, and may still reject it.
 */
function reachOnScreen(
  fs: { x: number; y: number },
  cs: { x: number; y: number },
  os: { x: number; y: number },
  inside: (p: { x: number; y: number }) => boolean,
): { x: number; y: number } {
  const middle = onCurve(fs, cs, os, MIDPOINT_T);
  if (inside(middle)) return middle;
  let near = 0;
  let far = MIDPOINT_T;
  for (let i = 0; i < REACH_STEPS; i++) {
    const mid = (near + far) / 2;
    if (inside(onCurve(fs, cs, os, mid))) near = mid; else far = mid;
  }
  return onCurve(fs, cs, os, near);
}

/** What the fan drew, so a caller can report it and a test can prove the layer ran. */
export interface FanDrawn {
  strands: number;
  bead: boolean;
  passages: number;
}

/**
 * Draw the open name's fan.
 *
 * `edges` is the open figure's own tie list. It is de-duplicated here rather than by the caller
 * because theirs is: a name tied twice to the same neighbour would otherwise be drawn twice as
 * bright, which is exactly the reading a soft tie is supposed to be denied.
 */
export function drawOpenFan(
  ctx: DrawContext,
  view: View,
  nodes: readonly FanNode[],
  focus: number,
  edges: readonly number[],
  fog: FanFog,
  zooms: FanLimits,
  chrome: FanChrome,
  seconds: number,
): FanDrawn {
  const drawn: FanDrawn = { strands: 0, bead: false, passages: 0 };
  const f = nodes[focus];
  if (f === undefined) return drawn;
  const z = view.cam.z;
  const W = view.view.width;
  const H = view.view.height;
  const fs = toScreen(view, f.x, f.y);
  const fr = Math.max(DISC_MIN, Math.min(DISC_MAX, f.r * z));
  const weak = fog.weak;
  const refs = fog.refs;

  const seen = new Set<number>();
  const strands: Strand[] = [];
  const passages: Passage[] = [];
  for (const id of edges) {
    if (seen.has(id)) continue;
    seen.add(id);
    const o = nodes[id];
    if (o === undefined) continue;
    const key = pairKey(focus, id);
    const soft = weak?.has(key) === true;
    const cls = fanClass(fog.walked(id), fog.seen(id), soft);
    const os = toScreen(view, o.x, o.y);
    /* The control point is pulled toward the origin, so the fan bows the way the whole web does. */
    const cs = toScreen(view, (f.x + o.x) * 0.5 * (1 - BEND), (f.y + o.y) * 0.5 * (1 - BEND));
    strands.push({ cls, ox: os.x, oy: os.y, cx: cs.x, cy: cs.y });
    const ref = refs?.get(key);
    /* A rumour carries no passage: there is no chapter and verse for a tie nobody stated. */
    if (ref !== undefined && cls < 2) {
      /*
       * THEIRS' MIDPOINT UNLESS THE TIE IS LONGER THAN ANYTHING THEIRS BUILDS.
       *
       * `0.25 * fs + 0.5 * cs + 0.25 * os` is the curve at t = 0.5, which is what `onCurve` returns
       * there and what this line used to say in full. On a local tie that is exactly their placement
       * and nothing below runs.
       */
      const world = Math.hypot(f.x - o.x, f.y - o.y);
      const spot = world > zooms.localTie
        ? reachOnScreen(fs, cs, os, p =>
            p.x >= REF_MARGIN && p.x <= W - REF_MARGIN
            && p.y >= chrome.barTop + REF_MARGIN && p.y <= H - chrome.barBot - REF_MARGIN)
        : onCurve(fs, cs, os, MIDPOINT_T);
      passages.push({
        ref,
        x: spot.x,
        y: spot.y,
        d: Math.hypot(os.x - fs.x, os.y - fs.y),
      });
    }
  }

  ctx.setLineDash([]);
  const gap = fr + GAP_AIR;
  for (const s of strands) {
    const far = s.cls === 2;
    /* A rumour is drawn as a leading stub rather than all the way across — `strand`'s `far`. */
    if (strand(ctx, fs.x, fs.y, s.cx, s.cy, s.ox, s.oy, gap, GAP_FAR, HEAD[s.cls] ?? 0,
      far ? WIDTH_FAR : WIDTH_NEAR, far)) {
      drawn.strands += 1;
    }
  }

  /*
   * ONE BEAD, ON ONE STRAND AT A TIME.
   *
   * It picks its strand from the clock rather than at random, and travels the same way every time,
   * so a reader who looks away and back finds it where they expect. Rumours are skipped: the bead
   * says "you can go this way", and a tie nobody stated is not a road.
   */
  const near = strands.filter(s => s.cls !== 2);
  const pick = near[Math.floor(seconds / BEAD_CYCLE) % near.length];
  if (pick !== undefined) {
    const len = Math.hypot(pick.ox - fs.x, pick.oy - fs.y);
    /*
     * It sets out from the edge of the disc, so it never appears from underneath the name.
     *
     * Their `Math.max(len,1)` is a divide-by-zero guard that CANNOT CHANGE THE ANSWER — kept
     * because it is theirs, recorded here because a reader will otherwise try to test it. The gap
     * is at least 24 (a disc floor of 4 plus 20 of air), so the moment a strand is short enough for
     * the guard to engage the ratio is already far past the cap of 0.9 and the cap has won. Every
     * mutation of it draws the identical frame, down to a zoom of 0.0004 where a tie is half a
     * pixel long. It becomes live only if the air or the cap is changed.
     */
    const from = Math.min(gap / Math.max(len, 1), BEAD_START_CAP);
    const phase = (seconds % BEAD_CYCLE) / BEAD_CYCLE;
    const u = from + (1 - from) * phase;
    const q = 1 - u;
    const bx = q * q * fs.x + 2 * q * u * pick.cx + u * u * pick.ox;
    const by = q * q * fs.y + 2 * q * u * pick.cy + u * u * pick.oy;
    /* Brightest halfway, gone at both ends, so it neither appears nor vanishes abruptly. */
    const alpha = Math.sin(phase * Math.PI) * BEAD_ALPHA;
    ctx.fillStyle = `rgba(${BEAD_COLOUR},${alpha.toFixed(3)})`;
    ctx.beginPath();
    ctx.arc(bx, by, BEAD_R, 0, TAU);
    ctx.fill();
    drawn.bead = true;
  }

  /*
   * THE SIX LONGEST TIES THAT CAN BE READ GET THEIR PASSAGE WRITTEN.
   *
   * Longest rather than nearest, because a long tie has room for the writing and a short one puts
   * it on top of both names. Six because more is a wall of text over the thing being read.
   *
   * ─────────────────────────────────────────────────────────────────────────────────────────────
   * THE ONE APPROVED DEVIATION FROM THEIR MECHANISM — Ariel, 2026-08-19, and the reasoning is
   * recorded in `V8-PORT.md` §5 rather than argued again here.
   *
   * Theirs takes the six longest and THEN tests the margins, so a tie that cannot be written
   * spends one of the six places anyway. Ours walks the ties longest-first and stops once six have
   * actually been WRITTEN, so an unwritable one costs nothing.
   *
   * The two rules agree exactly whenever all six longest are writable, which on their data is
   * almost always: `buildLocalTies` gives every one of their names two ties to its nearest
   * neighbours, so a fan is short ties with room beside them. Ours are semantic ties that cross
   * the world — Moses has 84 — and "the six longest" then selects precisely the ones whose
   * midpoints are nowhere near the screen. Measured before this change: every one of those 84 had
   * a citation and not one was ever written, at any zoom.
   *
   * This is the whole of the deviation. The order is still longest-first, the cap is still six,
   * every margin test is unchanged and runs in the same order — what changed is that the cap
   * counts what reached the reader instead of what was attempted.
   * ─────────────────────────────────────────────────────────────────────────────────────────────
   */
  if (z > zooms.refsFrom && passages.length > 0) {
    ctx.font = REF_FONT;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    let written = 0;
    for (const p of [...passages].sort((a, b) => b.d - a.d)) {
      if (written >= REF_CAP) break;
      /*
       * A cheap rejection before the expensive one. Measuring text is the costly part and a hub's
       * eighty ties would each pay for one every frame only to be thrown away. Deliberately weaker
       * than the real test below — `w / 2` is never negative, so anything this rejects the exact
       * test would reject too, and nothing writable is lost to it.
       */
      if (p.x < REF_MARGIN || p.x > W - REF_MARGIN) continue;
      if (p.y < chrome.barTop + REF_MARGIN || p.y > H - chrome.barBot - REF_MARGIN) continue;
      const w = ctx.measureText(p.ref).width;
      /* Off the edge of the map, or under the chrome: either way it cannot be read. */
      if (p.x < w / 2 + REF_MARGIN || p.x > W - w / 2 - REF_MARGIN) continue;
      if (chrome.blocked({ x0: p.x - w / 2, y0: p.y - REF_HIT_HALF, x1: p.x + w / 2, y1: p.y + REF_HIT_HALF })) continue;
      /* Its own dark plate, because it is written over a strand it must stay legible against. */
      ctx.fillStyle = REF_BOX;
      ctx.fillRect(p.x - w / 2 - REF_PAD_X, p.y - REF_BOX_TOP, w + REF_PAD_X * 2, REF_BOX_H);
      ctx.fillStyle = REF_INK;
      ctx.fillText(p.ref, p.x, p.y);
      written += 1;
      drawn.passages += 1;
    }
  }

  /*
   * TWO RINGS: one steady, one breathing.
   *
   * The steady one says which name is open. The breathing one is what finds it again after a pan,
   * because a moving mark is caught by the eye where a still one is not.
   */
  ctx.strokeStyle = RING_COLOUR;
  ctx.lineWidth = RING_WIDTH;
  ctx.beginPath();
  ctx.arc(fs.x, fs.y, fr + RING_OUT, 0, TAU);
  ctx.stroke();
  const swing = Math.sin(seconds * PULSE_RATE);
  ctx.strokeStyle = `rgba(99,224,232,${(PULSE_BASE + PULSE_SWING * swing).toFixed(3)})`;
  ctx.lineWidth = PULSE_WIDTH;
  ctx.beginPath();
  ctx.arc(fs.x, fs.y, fr + PULSE_OUT + swing * PULSE_SWELL, 0, TAU);
  ctx.stroke();
  return drawn;
}
